import React, { useState } from 'react'
import { Box, Button, Stack, Typography } from '@mui/material'
import { FormattedMessage } from 'react-intl'
import { useLocation, useNavigate } from 'react-router-dom'
import CookieGroup from './CookieGroup'
import {
  expiresDate,
  sendConsentEvent,
  updateAnalyticsCookies,
  updateFunctionalityCookies,
  updateMarketingCookies
} from '../../utils/cookies'
import { useCookies } from 'react-cookie'
import essentialCookies from './data/essentialCookies.json'
import preferenceCookies from './data/preferenceCookies.json'
import analysisCookies from './data/analysisCookies.json'
import marketingCookies from './data/marketingCookies.json'
import { NotificationManager } from 'src/functions/notifications'
import { useDispatch } from 'react-redux'
import { detectLanguage } from 'src/functions/detectLanguage'
import { detectAndSetLang } from 'src/utils'
import { IntlLink } from '../Common/IntlComponents'

const CookieSettings = () => {
  const [cookies, setCookie, removeCookie] = useCookies()
  const [disableButton, setDisableButton] = useState(true)
  const [selectedCookies, setSelectedCookies] = useState({
    analysis: cookies.analysisCookies ?? false,
    marketing: cookies.marketingCookies ?? false,
    functionality: cookies.functionalityCookies ?? false
  })
  const dispatch = useDispatch()
  const { pathname } = useLocation()
  const navigate = useNavigate()

  const cookiesConfig = [
    {
      key: 'essential',
      title: 'cookieSettings.essentialCookies.title',
      description: 'cookieSettings.essentialCookies.description',
      locked: true,
      updateFn: () => {},
      cookiesList: essentialCookies
    },
    {
      key: 'functionality',
      title: 'cookieSettings.preferenceCookies.title',
      description: 'cookieSettings.preferenceCookies.description',
      updateFn: () => {
        if (selectedCookies.functionality) {
          const { lang } = detectLanguage()
          detectAndSetLang({ lang, setCookie, dispatch, pathname, navigate })
        }
        updateFunctionalityCookies(selectedCookies.functionality ? 'granted' : 'denied')
      },

      cookiesList: preferenceCookies
    },
    {
      key: 'analysis',
      title: 'cookieSettings.analysisCookies.title',
      description: 'cookieSettings.analysisCookies.description',
      updateFn: () => updateAnalyticsCookies(selectedCookies.analysis ? 'granted' : 'denied', removeCookie),
      cookiesList: analysisCookies
    },
    {
      key: 'marketing',
      title: 'cookieSettings.marketingCookies.title',
      description: 'cookieSettings.marketingCookies.description',
      updateFn: () => updateMarketingCookies(selectedCookies.marketing ? 'granted' : 'denied', removeCookie),
      cookiesList: marketingCookies
    }
  ]

  const renderConfirmationMessage = () =>
    NotificationManager.success(<FormattedMessage id='notification.changesSaved' />)

  const handleChangeButton = (cookieKey: string) => {
    setDisableButton(false)
    setSelectedCookies((prevState) => ({
      ...prevState,
      [cookieKey]: !prevState[cookieKey as keyof typeof selectedCookies]
    }))
  }

  const handleConfirmChanges = () => {
    setCookie('hideCookiesBanner', true, { path: '/', expires: expiresDate })
    setDisableButton(true)

    cookiesConfig.forEach(({ key, updateFn }) => {
      const currentCookieValue = cookies[`${key}Cookies`]
      const newValue = selectedCookies[key as keyof typeof selectedCookies]

      // Only update cookie if value has changed
      if (currentCookieValue !== newValue) {
        setCookie(`${key}Cookies`, newValue, { path: '/', expires: expiresDate })
        if (updateFn) {
          updateFn()
        }
      }
    })
    sendConsentEvent('customize')
    renderConfirmationMessage()
  }

  return (
    <Box>
      <Typography gutterBottom>
        <FormattedMessage
          id='cookieSettings.description'
          values={{
            privacyPolicy: (
              <IntlLink to={'/legal/privacy'} target='_blank' rel='noopener noreferrer'>
                <FormattedMessage id='legal.privacy' />
              </IntlLink>
            ),
            cookiePolicy: (
              <IntlLink to={'/legal/cookies'} target='_blank' rel='noopener noreferrer'>
                <FormattedMessage id='legal.cookies' />
              </IntlLink>
            )
          }}
        />
      </Typography>
      <Stack gap={4}>
        {cookiesConfig.map(({ title, description, locked, key, cookiesList }) => (
          <React.Fragment key={key}>
            <CookieGroup
              title={title}
              description={description}
              locked={locked}
              handleChange={() => handleChangeButton(key)}
              checkboxValue={cookies[`${key}Cookies`]}
              cookiesList={cookiesList}
            />
          </React.Fragment>
        ))}
      </Stack>

      <Stack sx={{ alignItems: { md: 'end' }, mt: 4 }}>
        <Button disabled={disableButton} variant='contained' onClick={handleConfirmChanges}>
          <FormattedMessage id='cookies.confirmSelection' />
        </Button>
      </Stack>
    </Box>
  )
}

export default CookieSettings
